import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ClipboardCheck, Loader2, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, errMessage } from "@/lib/api";
import { Loading, ErrorBox } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { InspectionForm } from "@/components/inspection/InspectionForm";
import { InspectionView } from "@/components/inspection/InspectionView";
import { fmtDateTime } from "@/lib/time";
import { RESULT_KEY } from "@/lib/labels";
import { PdfButton } from "@/components/PdfButton";
import { useOfflineState } from "@/lib/offline/state";
import { SyncIcon } from "@/components/SyncIcon";
import { DeleteRecordButton } from "@/components/admin/DeletedRecords";

const ACTION_BTN = "h-11 flex-1 px-4 sm:flex-none";

export default function InspectionPage() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { online } = useOfflineState();
  const load = useCallback(() => {
    setError("");
    api.get(`/inspections/${id}`).then((r) => setDoc(r.data)).catch((e) => setError(errMessage(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (error) return <ErrorBox message={error} onRetry={load} />;
  if (!doc) return <Loading />;
  const again = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/inspections", { equipment_id: doc.equipment_id, team_id: doc.performed_by_team_id || null });
      navigate(`/inspections/${data.id}`);
    } catch (e) { toast.error(errMessage(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5">
      <div className="rise rounded-xl border border-border bg-card p-4 sm:p-6">
        <p className="font-data text-xs uppercase tracking-[0.2em] text-amber-400">Inspection · {doc.template.name}</p>
        <Link to={`/equipements/${doc.equipment_id}`} className="hover:underline">
          <h1 data-testid="inspection-title" className="mt-1 font-heading text-3xl font-extrabold uppercase sm:text-4xl"><span className="text-amber-300">{doc.equipment.code}</span> · {doc.equipment.name}</h1>
        </Link>
        <p className="text-sm text-muted-foreground">{doc.site.name} · {doc.user_name}</p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <StatusBadge status={RESULT_KEY(doc)} size="lg" testId="inspection-status-badge" />
          <SyncIcon id={doc.id} size="h-5 w-5" testId="inspection-sync-icon" />
          <span className="font-data text-xs text-slate-300">Début : {fmtDateTime(doc.started_at, doc.site)}</span>
          {doc.offline && <span data-testid="inspection-offline-marker" className="rounded border border-amber-500/60 px-2 py-0.5 text-xs text-amber-300">Heure de l'appareil (hors ligne){doc.synced_at ? ` · reçu ${fmtDateTime(doc.synced_at, doc.site)}` : " · en attente d'envoi"}</span>}
          {doc.finalized_at && <span data-testid="inspection-finalized-at" className="font-data text-xs text-slate-300">Finalisée : {fmtDateTime(doc.finalized_at, doc.site)}</span>}
          {!doc.can_edit && online && !doc.sync_conflict && <PdfButton url={`/inspections/${doc.id}/pdf`} testId="inspection-pdf-button" />}
          {user?.role === "admin" && online && <DeleteRecordButton kind="inspection" id={doc.id} onDeleted={() => navigate(`/equipements/${doc.equipment_id}`)} />}
        </div>
        {doc.sync_conflict && <p data-testid="inspection-sync-conflict" role="alert" className="mt-3 rounded-md border border-red-500/60 bg-red-950/40 p-3 text-sm text-red-100">Synchronisation en conflit : {doc.sync_conflict} Le formulaire est verrouillé ; résolvez le conflit dans le bandeau en haut de page.</p>}
      </div>
      {!doc.can_edit && !doc.sync_conflict && (
        <div data-testid="inspection-actions-row" className="flex flex-wrap gap-2">
          {doc.user_id === user?.id && (
            <Button data-testid="inspection-again-button" onClick={again} disabled={busy} className={`${ACTION_BTN} bg-emerald-600 text-white hover:bg-emerald-500`}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ClipboardCheck className="mr-2 h-4 w-4" />} Inspecter à nouveau
            </Button>
          )}
          <Button asChild variant="outline" className={`${ACTION_BTN} border-amber-500/60 text-amber-500 hover:bg-amber-500/10`}>
            <Link to={`/equipements/${doc.equipment_id}`} data-testid="inspection-equipment-button"><Wrench className="mr-2 h-4 w-4" /> Voir l'équipement</Link>
          </Button>
        </div>
      )}
      {doc.can_edit ? <InspectionForm doc={doc} onDone={setDoc} /> : <InspectionView doc={doc} />}
    </div>
  );
}
